import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BlogPostStatusEnum } from '../../../@types/BlogPost';
import type { NewsletterForPDF } from '../../../utils/newsletterPDF';
import { renderNewsletterToPDFBlob } from '../../../utils/newsletterPDF';
import { ROUTES } from '../../../utils/routes';
import Documents from './sections/Documents';
import Missions from './sections/Missions';
import Presentation from './sections/Presentation';
import Scripts from './sections/Scripts';

type SectionId = 'presentation' | 'missions' | 'scripts' | 'documents';

type Step = {
  id: SectionId;
  title: string;
  emoji: string;
};

function ReferentFormation() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<SectionId>('presentation');
  const [isExporting, setIsExporting] = useState(false);

  const steps = useMemo<Step[]>(
    () => [
      { id: 'presentation', title: '1 - PRÉSENTATION', emoji: `<span role="img" aria-label="presentation">📋</span>` },
      { id: 'missions', title: '2 - RÔLE / MISSIONS', emoji: `<span role="img" aria-label="star">🌟</span>` },
      { id: 'scripts', title: `3 - SCRIPT D'APPEL`, emoji: `<span role="img" aria-label="telephone">📞</span>` },
      { id: 'documents', title: '4 - TABLEAU DOCUMENTS', emoji: `<span role="img" aria-label="documents">📄</span>` },
    ],
    [],
  );

  const handleGoBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
      }
    } catch (error) {
      console.error('Navigation error :', error);
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
    }
  };

  const handleDownloadPDF = async() => {
    if (isExporting) {return;}
    setIsExporting(true);

    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const dummyNewsletter: NewsletterForPDF = {
        id: 'referent-formation',
        subject: 'Formation référent',
        content: '',
        status: BlogPostStatusEnum.PUBLIC,
        createdAt: new Date().toISOString(),
      };

      const { blob } = await renderNewsletterToPDFBlob(dummyNewsletter);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Formation referent croissance declarations juridiques.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF Export error:', error);
      alert('Une erreur est survenue lors de la génération du PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const showContent = (contentId: SectionId) => {
    setActiveSection(contentId);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'presentation':
        return <Presentation />;
      case 'missions':
        return <Missions />;
      case 'scripts':
        return <Scripts />;
      case 'documents':
        return <Documents />;
      default:
        return <Presentation />;
    }
  };

  return (
    <div className='rounded-lg bg-white p-8 shadow-md'>
      <div className="mb-8 flex items-center justify-between">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>

        <button
          onClick={handleDownloadPDF}
          disabled={isExporting}
          className="flex items-center gap-2 rounded-lg bg-rgpd-blue px-4 py-2 text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isExporting ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span>Génération...</span>
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Télécharger en PDF</span>
            </>
          )}
        </button>
      </div>

      <div className="min-h-screen leading-relaxed text-slate-800">
        <div className="relative mx-5 mt-5 mb-0 overflow-hidden rounded-2xl bg-rgpd-blue px-[30px] py-5 text-center text-white">
          <div className="relative z-10">
            <h1 className="mb-2 text-xl font-bold tracking-tight">
              PRÉALABLE POUR LE RÉFÉRENT MUTATION LOCATION :
            </h1>
            <div className="mx-auto max-w-2xl text-base font-normal opacity-90">
              Présentation et rôle, mission, argumentaire, tableau des documents à connaitre
            </div>
          </div>
        </div>

        <div className="sticky top-5 z-20 mx-5 mt-6">
          <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100 md:grid-cols-4">
            {steps.map(step => (
              <button
                key={step.id}
                onClick={() => showContent(step.id)}
                className={`flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 px-6 py-5 transition-all duration-300 hover:-translate-y-0.5 ${
                  activeSection === step.id
                    ? 'border-b-8 border-rgpd-green bg-white font-bold text-slate-800'
                    : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500'
                }`}
              >
                <span className="text-xl" dangerouslySetInnerHTML={{ __html: step.emoji }} />
                <span className="text-center leading-relaxed">{step.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-8 pb-15">
          <div className={`rounded-3xl border border-slate-200/70 bg-white ${activeSection === 'scripts' ? 'overflow-visible' : 'overflow-hidden'}`}>
            <div className="p-8">{renderContent()}</div>
          </div>
        </div>
      </div>

      {/* Hidden PDF Export Container */}
      {isExporting && (
        <div style={{ position: 'absolute', left: '-10000px', top: '-10000px' }}>
          <div id="newsletter-content">
            <div className="newsletter-content" style={{ width: '210mm', backgroundColor: 'white', margin: '0 auto', padding: '40px' }}>
              <div style={{ textAlign: 'center', marginBottom: '40px', borderBottom: '2px solid #1e3a8a', paddingBottom: '20px' }}>
                <h1 style={{ color: '#1e3a8a', fontSize: '24px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  PRÉALABLE POUR LE RÉFÉRENT MUTATION LOCATION
                </h1>
                <p style={{ fontSize: '14px', color: '#64748b', marginTop: '10px' }}>
                  Présentation et rôle, mission, argumentaire, tableau des documents à connaitre
                </p>
              </div>

              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#1e3a8a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
                  1 - PRÉSENTATION
                </h2>
                <Presentation />
              </div>

              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#1e3a8a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
                  2 - RÔLE / MISSIONS
                </h2>
                <Missions />
              </div>

              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#1e3a8a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
                  3 - SCRIPT D'APPEL
                </h2>
                <Scripts />
              </div>

              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#1e3a8a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
                  4 - TABLEAU DOCUMENTS
                </h2>
                <Documents />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReferentFormation;
